import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, getToken } from '../lib/api';
import { useClients } from '../lib/lookups';

interface ReportRow { [k: string]: unknown }
interface ReportResp { mode: string; total: number; page: number; pageSize: number; rows: ReportRow[] }

const PAGE_SIZE = 50;
const MODE = 'wip';

const getLocalDateString = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const localDate = new Date(d.getTime() - (offset * 60 * 1000));
  return localDate.toISOString().slice(0, 10);
};

export function WipReportPage() {
  const today = getLocalDateString();
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [applied, setApplied] = useState(false);

  const clients = useClients();

  function buildParams(): URLSearchParams {
    const sp = new URLSearchParams({ mode: MODE, page: String(page), pageSize: String(PAGE_SIZE) });
    if (q) sp.set('q', q);
    if (product) sp.set('product', product);
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    if (clientIds.length) sp.set('clients', clientIds.join(','));
    return sp;
  }

  const report = useQuery({
    enabled: applied,
    queryKey: ['wip-report', { q, product, from, to, clientIds, page }],
    queryFn: () => api<ReportResp>(`/api/reports?${buildParams().toString()}`),
  });

  function apply(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setApplied(true);
  }

  const [exporting, setExporting] = useState(false);

  function exportAs(ext: 'csv' | 'xlsx') {
    const sp = new URLSearchParams({ mode: MODE });
    if (q) sp.set('q', q);
    if (product) sp.set('product', product);
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    if (clientIds.length) sp.set('clients', clientIds.join(','));
    const token = getToken();
    setExporting(true);
    fetch(`/api/reports/export.${ext}?${sp.toString()}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      if (!r.ok) {
        const msg = await r.text().catch(() => '');
        throw new Error(msg || `Export failed (${r.status})`);
      }
      return r.blob();
    }).then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `wip-track-report-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch((e: Error) => {
      alert(`Export failed: ${e.message}`);
    }).finally(() => setExporting(false));
  }

  const rows = report.data?.rows ?? [];
  const total = report.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const headers = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-brand">WIP Track Records</h1>
        <p className="text-xs text-slate-500">Work-in-progress tracking: job orders with PO, production, dispatch, and balance details. Filter by date, client, or product.</p>
      </div>

      <form className="card" onSubmit={apply}>
        <div className="card-header">Filter</div>
        <div className="card-body grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="label">Search (Job ID, PO No, Job Name, Client)</label>
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Free-text search…" />
          </div>
          <div>
            <label className="label">From</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <div className="md:col-span-4">
            <label className="label">Product (name or Product ID)</label>
            <input className="input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. REKO-25 or PRU15586" />
          </div>

          <div className="md:col-span-4">
            <label className="label">Clients (leave empty for all)</label>
            <div className="max-h-32 overflow-auto rounded border border-slate-200 p-2 grid gap-1 sm:grid-cols-3 lg:grid-cols-4 text-xs">
              {clients.data?.map((c) => (
                <label key={c.id} className="flex items-center gap-2">
                  <input type="checkbox"
                    checked={clientIds.includes(c.id)}
                    onChange={(e) => {
                      setClientIds((cur) =>
                        e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id),
                      );
                    }} />
                  <span className="truncate" title={c.name}>{c.name}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-500">{clientIds.length} selected</p>
          </div>

          <div className="md:col-span-4 flex flex-wrap items-center gap-2">
            <button type="submit" className="btn-primary">Run report</button>
            {applied && <button type="button" className="btn-primary bg-emerald-600 hover:bg-emerald-700 text-white" disabled={exporting} onClick={() => exportAs('xlsx')}>{exporting ? 'Exporting…' : 'Export Excel'}</button>}
            {applied && <button type="button" className="btn-ghost" disabled={exporting} onClick={() => exportAs('csv')}>{exporting ? 'Exporting…' : 'Export CSV'}</button>}
            <button type="button" className="btn-ghost text-xs" onClick={() => { setFrom(today); setTo(today); }}>Set to Today</button>
            {(from || to) && <button type="button" className="btn-ghost text-xs text-red-500" onClick={() => { setFrom(''); setTo(''); }}>Clear Dates</button>}
          </div>
        </div>
      </form>

      {applied && (
        <div className="card overflow-hidden">
          <div className="card-header flex justify-between">
            <span>Results</span>
            <span className="text-xs font-normal">{total.toLocaleString()} rows</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>{headers.map((h) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {report.isLoading && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-slate-500">Loading…</td></tr>}
                {report.isError && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-red-600">Failed: {(report.error as Error).message}</td></tr>}
                {!report.isLoading && rows.length === 0 && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-slate-500">No records.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    {headers.map((h) => (
                      <td key={h} className="px-3 py-2 text-slate-700 text-xs whitespace-nowrap">
                        {formatVal(r[h])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center p-3 text-sm">
            <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
            <div className="flex gap-2">
              <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
              <button className="btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatVal(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return v;
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}
